import type { FunctionalComponent } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { useStore } from '../store';
import type { Country } from '../utils/phone';
import {
  COUNTRIES,
  parseFullPhone,
  formatPhoneNumber,
} from '../utils/phone';

function filterCountries(query: string): Country[] {
  if (!query.trim()) return COUNTRIES;
  const q = query.toLowerCase().trim();
  return COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q),
  );
}

function isWidgetContactComplete(name: string, email: string): boolean {
  return name.trim().includes(' ') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export const ContactForm: FunctionalComponent = () => {
  const { state, dispatch } = useStore();
  const [name, setName] = useState(state.contact.name);
  const [email, setEmail] = useState(state.contact.email);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useFullForm, setUseFullForm] = useState(
    () => !isWidgetContactComplete(state.contact.name, state.contact.email),
  );

  const parsed = parseFullPhone(state.contact.phone);
  const [selectedCountry, setSelectedCountry] = useState(parsed.country);
  const [nationalDigits, setNationalDigits] = useState(parsed.nationalDigits);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const p = parseFullPhone(state.contact.phone);
    setSelectedCountry(p.country);
    setNationalDigits(p.nationalDigits);
  }, [state.contact.phone]);

  useEffect(() => {
    if (!countryOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const panelHeight = 260;
    const openUp = spaceBelow < panelHeight && rect.top > spaceBelow;
    setPanelStyle({
      top: openUp ? rect.top - panelHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
    });
  }, [countryOpen]);

  useEffect(() => {
    if (!countryOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
        setPanelStyle(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryOpen]);

  const filteredCountries = filterCountries(countrySearch);
  const displayValue = formatPhoneNumber(nationalDigits, selectedCountry.format);
  const maxDigits = (() => {
    const mask = selectedCountry.format;
    if (!mask) return 15;
    return (mask.match(/X/g) || []).length + 4;
  })();

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Full name is required';
    else if (!name.trim().includes(' ')) e.name = 'Please enter first and last name';
    if (!email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: Event) {
    ev.preventDefault();
    if (!validate()) return;
    const fullPhone = nationalDigits.trim()
      ? selectedCountry.dialCode + nationalDigits.replace(/\D/g, '')
      : '';
    dispatch({
      type: 'SET_CONTACT',
      contact: { name: name.trim(), email: email.trim(), phone: fullPhone },
    });
    dispatch({ type: 'SET_STEP', step: 'payment' });
  }

  function handlePhoneInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const digits = target.value.replace(/\D/g, '').slice(0, maxDigits);
    setNationalDigits(digits);
  }

  function handleCountrySelect(c: Country) {
    setSelectedCountry(c);
    setCountryOpen(false);
    setCountrySearch('');
    setPanelStyle(null);
  }

  const savedOk = isWidgetContactComplete(state.contact.name, state.contact.email);
  const showReview = savedOk && !useFullForm;
  const phoneDisplay = (() => {
    if (!state.contact.phone?.trim()) return null;
    const p = parseFullPhone(state.contact.phone);
    return formatPhoneNumber(p.nationalDigits, p.country.format) || state.contact.phone;
  })();

  function handleContinueWithSaved() {
    dispatch({ type: 'SET_STEP', step: 'payment' });
  }

  function handleUseDifferentDetails() {
    setName(state.contact.name);
    setEmail(state.contact.email);
    const p = parseFullPhone(state.contact.phone);
    setSelectedCountry(p.country);
    setNationalDigits(p.nationalDigits);
    setUseFullForm(true);
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div class="mw-section-title" style={{ padding: 0, marginBottom: '1rem' }}>
        Contact Information
      </div>
      {showReview ? (
        <div>
          <p class="mw-contact-review-lead">We will use these details for this order:</p>
          <div class="mw-contact-review-card" role="status">
            <div class="mw-contact-review-row">
              <span class="mw-contact-review-k">Name</span>
              <span class="mw-contact-review-v">{state.contact.name}</span>
            </div>
            <div class="mw-contact-review-row">
              <span class="mw-contact-review-k">Email</span>
              <span class="mw-contact-review-v">{state.contact.email}</span>
            </div>
            {phoneDisplay && (
              <div class="mw-contact-review-row">
                <span class="mw-contact-review-k">Phone</span>
                <span class="mw-contact-review-v">{phoneDisplay}</span>
              </div>
            )}
          </div>
          <button
            type="button"
            class="mw-btn mw-btn--primary mw-btn--block"
            style={{ marginTop: '0.75rem' }}
            onClick={handleContinueWithSaved}
          >
            Continue to payment
          </button>
          <button
            type="button"
            class="mw-btn mw-btn--outline mw-btn--block"
            style={{ marginTop: '0.5rem' }}
            onClick={handleUseDifferentDetails}
          >
            Use different details
          </button>
        </div>
      ) : (
      <form onSubmit={handleSubmit} noValidate>
        <div class="mw-field">
          <label for="mw-name">Full Name *</label>
          <input
            id="mw-name"
            type="text"
            placeholder="Jane Smith"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            autocomplete="name"
          />
          {errors.name && <div class="mw-field-error">{errors.name}</div>}
        </div>
        <div class="mw-field">
          <label for="mw-email">Email Address *</label>
          <input
            id="mw-email"
            type="email"
            placeholder="jane@example.com"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            autocomplete="email"
          />
          {errors.email && <div class="mw-field-error">{errors.email}</div>}
        </div>
        <div class="mw-field">
          <label for="mw-phone">Phone (optional)</label>
          <div class="mw-phone-row">
            <div class="mw-phone-country-dropdown" ref={countryDropdownRef}>
              <button
                ref={triggerRef}
                type="button"
                class="mw-phone-country-trigger"
                onClick={() => setCountryOpen(!countryOpen)}
                aria-expanded={countryOpen}
                aria-haspopup="listbox"
                aria-label="Country code"
              >
                <span>{selectedCountry.flag}</span>
                <span>{selectedCountry.dialCode}</span>
                <span class="mw-phone-country-chevron">{countryOpen ? '▲' : '▼'}</span>
              </button>
              {countryOpen && panelStyle && (
                <div
                  class="mw-phone-country-panel"
                  style={{
                    position: 'fixed',
                    top: `${panelStyle.top}px`,
                    left: `${panelStyle.left}px`,
                    width: `${panelStyle.width}px`,
                  }}
                >
                  <input
                    type="text"
                    class="mw-phone-country-search"
                    placeholder="Search by country name..."
                    value={countrySearch}
                    onInput={(e) => setCountrySearch((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    autocomplete="off"
                  />
                  <div class="mw-phone-country-list" role="listbox">
                    {filteredCountries.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        role="option"
                        aria-selected={c.code === selectedCountry.code}
                        class={`mw-phone-country-option${c.code === selectedCountry.code ? ' mw-phone-country-option--selected' : ''}`}
                        onClick={() => handleCountrySelect(c)}
                      >
                        <span>{c.flag}</span>
                        <span>{c.dialCode}</span>
                        <span>{c.name}</span>
                      </button>
                    ))}
                    {filteredCountries.length === 0 && (
                      <div class="mw-phone-country-empty">No countries match your search</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <input
              id="mw-phone"
              type="tel"
              placeholder={selectedCountry.format ? formatPhoneNumber('5551234567', selectedCountry.format) : '+1 555 123 4567'}
              value={displayValue}
              onInput={handlePhoneInput}
              autocomplete="tel"
              class="mw-phone-input"
            />
          </div>
        </div>
        <button type="submit" class="mw-btn mw-btn--primary mw-btn--block">
          Continue to Payment
        </button>
      </form>
      )}
    </div>
  );
};
